import React, { useEffect, useMemo, useRef, useState } from "react";
import { IMaskInput } from "react-imask";
import {
  addDays,
  addMonths,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  isValid,
  parse,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import ru from "date-fns/locale/ru";
import "./PeriodFilter.css";

const DATE_FORMAT = "dd.MM.yyyy";
const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
/** 6 недель, чтобы высота календаря не прыгала между месяцами */
const CALENDAR_DAYS = 42;

const formatDate = (date) => (date ? format(date, DATE_FORMAT) : "");

const parseInput = (text) => {
  if (!text || text.length !== DATE_FORMAT.length) return null;
  const parsed = parse(text, DATE_FORMAT, new Date());
  return isValid(parsed) && parsed.getFullYear() >= 1900 ? parsed : null;
};

const DEFAULT_PLACEHOLDER = "Период";

const getRangeLabel = (dateFrom, dateTo) => {
  if (dateFrom && dateTo) return `${formatDate(dateFrom)} – ${formatDate(dateTo)}`;
  if (dateFrom) return `с ${formatDate(dateFrom)}`;
  if (dateTo) return `по ${formatDate(dateTo)}`;
  return "";
};

/** Своя подпись (например «Дата начала») остаётся префиксом и после выбора дат */
const getTriggerLabel = (dateFrom, dateTo, placeholder) => {
  const range = getRangeLabel(dateFrom, dateTo);
  if (!range) return placeholder;
  return placeholder === DEFAULT_PLACEHOLDER ? range : `${placeholder}: ${range}`;
};

const getMonthLabel = (month) => {
  const label = format(month, "LLLL yyyy", { locale: ru });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const getCalendarDays = (month) => {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  return Array.from({ length: CALENDAR_DAYS }, (_, i) => addDays(start, i));
};

function DateField({ label, value, onCommit }) {
  const [text, setText] = useState(formatDate(value));
  const [isInvalid, setIsInvalid] = useState(false);

  useEffect(() => {
    setText(formatDate(value));
    setIsInvalid(false);
  }, [value]);

  const handleAccept = (next) => {
    if (next === text) return;
    setText(next);

    if (!next) {
      setIsInvalid(false);
      if (value) onCommit(null);
      return;
    }
    if (next.length < DATE_FORMAT.length) {
      setIsInvalid(false);
      return;
    }

    const parsed = parseInput(next);
    setIsInvalid(!parsed);
    if (parsed && !(value && isSameDay(parsed, value))) onCommit(parsed);
  };

  const handleBlur = () => {
    if (text && !parseInput(text)) {
      setText(formatDate(value));
      setIsInvalid(false);
    }
  };

  return (
    <div className="ojp-field">
      <span className="ojp-field__label">{label}</span>
      <div className={`ojp-field__control${isInvalid ? " is-invalid" : ""}`}>
        <IMaskInput
          className="ojp-field__input"
          mask="00.00.0000"
          value={text}
          unmask={false}
          onAccept={handleAccept}
          onBlur={handleBlur}
          placeholder="ДД.ММ.ГГГГ"
          inputMode="numeric"
          aria-label={`${label} (ДД.ММ.ГГГГ)`}
        />
        {text && (
          <button
            type="button"
            className="ojp-field__clear"
            aria-label={`Очистить поле «${label}»`}
            onClick={() => {
              setText("");
              setIsInvalid(false);
              onCommit(null);
            }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path
                d="M1.5 1.5L8.5 8.5M8.5 1.5L1.5 8.5"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

function PeriodFilter({ dateFrom, dateTo, onChange, placeholder = DEFAULT_PLACEHOLDER }) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()));
  const [hoverDate, setHoverDate] = useState(null);
  const containerRef = useRef(null);
  const isActive = Boolean(dateFrom || dateTo);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      setViewMonth(startOfMonth(dateFrom || dateTo || new Date()));
      setHoverDate(null);
    }
    setIsOpen((open) => !open);
  };

  const commitRange = (from, to) => {
    if (from && to && isAfter(from, to)) {
      onChange({ dateFrom: to, dateTo: from });
    } else {
      onChange({ dateFrom: from, dateTo: to });
    }
  };

  const handleDayClick = (day) => {
    if (dateFrom && !dateTo) {
      commitRange(dateFrom, day);
    } else if (!dateFrom && dateTo) {
      commitRange(day, dateTo);
    } else {
      onChange({ dateFrom: day, dateTo: null });
    }
    if (!isSameMonth(day, viewMonth)) setViewMonth(startOfMonth(day));
  };

  const handleFieldCommit = (field) => (date) => {
    if (field === "from") commitRange(date, dateTo);
    else commitRange(dateFrom, date);
    if (date) setViewMonth(startOfMonth(date));
  };

  const days = useMemo(() => getCalendarDays(viewMonth), [viewMonth]);

  // Пока выбран только один край, диапазон подсвечивается до дня под курсором
  const previewEnd = dateFrom && !dateTo ? hoverDate : null;
  const previewStart = !dateFrom && dateTo ? hoverDate : null;
  const rangeStart = dateFrom || previewStart;
  const rangeEnd = dateTo || previewEnd;
  const [lower, upper] =
    rangeStart && rangeEnd && isAfter(rangeStart, rangeEnd)
      ? [rangeEnd, rangeStart]
      : [rangeStart, rangeEnd];

  const getDayClassName = (day) => {
    const classes = ["ojp-day"];
    if (!isSameMonth(day, viewMonth)) classes.push("is-outside");
    if (isToday(day)) classes.push("is-today");

    const isStart = lower && isSameDay(day, lower);
    const isEnd = upper && isSameDay(day, upper);
    if (isStart) classes.push("is-range-start");
    if (isEnd) classes.push("is-range-end");
    if (lower && upper && !isSameDay(lower, upper)) {
      if (isStart || isEnd || (isAfter(day, lower) && isBefore(day, upper))) {
        classes.push("is-in-range");
      }
    }
    if ((dateFrom && isSameDay(day, dateFrom)) || (dateTo && isSameDay(day, dateTo))) {
      classes.push("is-selected");
    }
    return classes.join(" ");
  };

  return (
    <div className="ojp" ref={containerRef}>
      <button
        type="button"
        className={`ojp-trigger${isActive ? " is-active" : ""}${
          isOpen ? " is-open" : ""
        }`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={handleToggle}
      >
        <svg
          className="ojp-trigger__icon"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <rect
            x="2"
            y="3"
            width="12"
            height="11"
            rx="1.5"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M2 6.5H14M5.5 2V4M10.5 2V4"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </svg>
        <span className="ojp-trigger__label">
          {getTriggerLabel(dateFrom, dateTo, placeholder)}
        </span>
        <svg
          className="ojp-trigger__chevron"
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
        >
          <path
            d="M1 1.5L6 6.5L11 1.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="ojp-popover" role="dialog" aria-label="Выбор периода">
          <div className="ojp-fields">
            <DateField
              label="От"
              value={dateFrom}
              onCommit={handleFieldCommit("from")}
            />
            <span className="ojp-fields__dash">—</span>
            <DateField
              label="До"
              value={dateTo}
              onCommit={handleFieldCommit("to")}
            />
          </div>

          <div className="ojp-calendar">
            <div className="ojp-calendar__header">
              <button
                type="button"
                className="ojp-calendar__nav"
                aria-label="Предыдущий месяц"
                onClick={() => setViewMonth((m) => addMonths(m, -1))}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M10 12L6 8L10 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <span className="ojp-calendar__title">
                {getMonthLabel(viewMonth)}
              </span>
              <button
                type="button"
                className="ojp-calendar__nav"
                aria-label="Следующий месяц"
                onClick={() => setViewMonth((m) => addMonths(m, 1))}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M6 4L10 8L6 12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="ojp-calendar__weekdays">
              {WEEKDAYS.map((weekday, index) => (
                <span
                  key={weekday}
                  className={`ojp-calendar__weekday${index > 4 ? " is-weekend" : ""}`}
                >
                  {weekday}
                </span>
              ))}
            </div>

            <div
              className="ojp-calendar__grid"
              onMouseLeave={() => setHoverDate(null)}
            >
              {days.map((day) => (
                <button
                  key={day.toISOString()}
                  type="button"
                  className={getDayClassName(day)}
                  onClick={() => handleDayClick(day)}
                  onMouseEnter={() => setHoverDate(day)}
                  aria-label={format(day, "d MMMM yyyy", { locale: ru })}
                >
                  <span className="ojp-day__num">{format(day, "d")}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="ojp-footer">
            <button
              type="button"
              className="ojp-footer__reset"
              disabled={!isActive}
              onClick={() => onChange({ dateFrom: null, dateTo: null })}
            >
              Сбросить
            </button>
            <button
              type="button"
              className="ojp-footer__apply"
              onClick={() => setIsOpen(false)}
            >
              Готово
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PeriodFilter;
